import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { useEffect, useState } from "react"

import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

import Modal from "@/components/Modal"
import SkeletonTable from "@/components/SkeletonTable"
import { useDebounce } from "@/hooks/useDebounce"
import {
  DataSiswa,
  PaginationData,
  VerBerkasPascaKelompokDetail,
} from "@/types"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"
import FormPenilaian from "./FormPenilaian"
import { Pagination } from "./Pagination"

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  paginationData: PaginationData<DataSiswa>
  isPending: boolean
}

export function DataTable<TData, TValue>({
  columns,
  data,
  paginationData,
  isPending,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const [rowSelection, setRowSelection] = useState({})

  const [searchParams] = useSearchParams()
  const [searchQuery, setSearchQuery] = useState(
    searchParams.get("search") || ""
  )
  const [showForm, setShowForm] = useState(false)
  const [selectedDataPeserta, setSelectedDataPeserta] =
    useState<VerBerkasPascaKelompokDetail | null>(null)

  const debouncedQuery = useDebounce(searchQuery, 1000)
  const navigate = useNavigate()
  const location = useLocation()

  const statusParams = searchParams.get("status")

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  })

  useEffect(() => {
    const params = new URLSearchParams(location.search)

    if (debouncedQuery) {
      if (params.get("search") !== debouncedQuery) {
        params.delete("page")
      }

      if (
        params.get("search") !== debouncedQuery ||
        params.get("page") !== "1"
      ) {
        params.set("search", debouncedQuery)
        navigate(`${location.pathname}?${params.toString()}`, {
          replace: true,
        })
      }
    } else {
      if (params.has("search") || params.get("page") !== "1") {
        params.delete("search")
        navigate(`${location.pathname}?${params.toString()}`, {
          replace: true,
        })
      }
    }
  }, [debouncedQuery, navigate, location.pathname, location.search])

  return (
    <div>
      <div className="flex items-center justify-between gap-3 py-4">
        <div className="flex-1 flex items-center gap-x-3">
          <Input
            autoFocus
            placeholder="Filter by nama lengkap, nik, nisn, email, no handpone..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            className="max-w-sm"
          />
          <button className="rounded-full border flex text-sm font-semibold overflow-hidden">
            <div
              className={`py-2 px-4 whitespace-nowrap ${
                statusParams === "belum"
                  ? "bg-black/90 text-white rounded-full"
                  : "hover:bg-secondary"
              }`}
              onClick={() => {
                if (statusParams) searchParams.delete("status")
                searchParams.append("status", "belum")

                navigate(`${location.pathname}?${searchParams.toString()}`)
              }}
            >
              Belum Dinilai
            </div>
            <div
              className={`py-2 px-4 whitespace-nowrap ${
                statusParams === "sudah"
                  ? "bg-black/90 text-white rounded-full"
                  : "hover:bg-secondary"
              }`}
              onClick={() => {
                if (statusParams) searchParams.delete("status")
                searchParams.append("status", "sudah")

                navigate(`${location.pathname}?${searchParams.toString()}`)
              }}
            >
              Sudah Dinilai
            </div>
            <div
              className={`py-2 px-4 whitespace-nowrap ${
                !statusParams
                  ? "bg-black/90 text-white rounded-full"
                  : "hover:bg-secondary"
              }`}
              onClick={() => {
                if (statusParams) searchParams.delete("status")

                navigate(`${location.pathname}?${searchParams.toString()}`)
              }}
            >
              Semua
            </div>
          </button>
        </div>
        <div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="ml-auto">
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => {
                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      className="capitalize"
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) =>
                        column.toggleVisibility(!!value)
                      }
                    >
                      {column.id}
                    </DropdownMenuCheckboxItem>
                  )
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      {isPending ? (
        <SkeletonTable />
      ) : (
        <div className="border rounded-xl">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header, index) => {
                    if (index === 0) {
                      return <TableHead></TableHead>
                    }
                    return (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </TableHead>
                    )
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                  >
                    {row.getVisibleCells().map((cell, index) =>
                      index === 0 ? (
                        <TableCell>
                          {/* <Penilaian
                            dataPeserta={
                              row.original as VerBerkasPascaKelompokDetail
                            }
                          /> */}
                          <Button
                            onClick={() => {
                              setShowForm(true)
                              setSelectedDataPeserta(
                                row.original as VerBerkasPascaKelompokDetail
                              )
                            }}
                          >
                            Penilaian
                          </Button>
                        </TableCell>
                      ) : (
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      )
                    )}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No results.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
      <br />
      <div>
        <Pagination table={table} paginationData={paginationData} />
      </div>

      <Modal
        show={showForm}
        onClose={() => setShowForm(false)}
        className="w-[95vw] h-[95vh]"
      >
        <FormPenilaian
          dataPeserta={selectedDataPeserta as VerBerkasPascaKelompokDetail}
        />
        <div className="absolute bottom-3 left-3 right-3 mt-5">
          <div className="w-full">
            <Button className="w-full" onClick={() => setShowForm(false)}>
              Tutup
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
